import React, { useMemo, useState } from "react";
import { Box, Typography, Button, Select, MenuItem, TextField } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import { toast } from "react-toastify";
import { ApplicationType, entity_id, locationGeo, Platform } from "../Utils/Const";
import useIPAddress from "../Utils/useIPAddress";
import { baseapi } from "../Environmentalurl";
import api from "../Utils/Api";

const errorToastStyle = { background: "red", color: "white", fontWeight: "bold", borderRadius: "8px" };
const successToastStyle = { background: "green", color: "white", fontWeight: "bold", borderRadius: "8px" };

const getRupaySecurityQuestions = () => {
  const tryParse = (raw) => {
    try {
      return typeof raw === "string" ? JSON.parse(raw) : raw;
    } catch {
      return null;
    }
  };
  const rawQs =
    sessionStorage.getItem("RupaySecurityQuestions") ||
    localStorage.getItem("RupaySecurityQuestions");
  const parsedQs = tryParse(rawQs);
  if (Array.isArray(parsedQs)) return parsedQs;
  return [];
};

export default function SetSecurityQuestionsSetup({ mobileNumber, rupayRefNo, onSuccess, onBack }) {
  const ip = useIPAddress();
  const sessionId = sessionStorage.getItem("SessionID");

  const master = useMemo(
    () =>
      getRupaySecurityQuestions()
        .filter((x) => x?.IsActive)
        .map((x) => x.Question)
        .filter(Boolean),
    [],
  );

  const [step, setStep] = useState(0); // 0,1,2
  const [q, setQ] = useState(["", "", ""]);
  const [a, setA] = useState(["", "", ""]);
  const [saving, setSaving] = useState(false);

  const setQuestionAt = (idx, val) =>
    setQ((prev) => {
      const next = [...prev];
      next[idx] = val;
      return next;
    });

  const setAnswerAt = (idx, val) =>
    setA((prev) => {
      const next = [...prev];
      next[idx] = val.slice(0, 25);
      return next;
    });

  // options for a step, hiding questions picked in the other steps
  const optionsFor = (idx) =>
    master.filter((question) => !q.some((sel, i) => i !== idx && sel === question));

  const stepValid = (idx) => q[idx] && q[idx].trim() && a[idx] && a[idx].trim();

  const allValid = useMemo(() => {
    const filled = q.every((x) => x && x.trim()) && a.every((x) => x && x.trim());
    const noDupes = new Set(q).size === q.length;
    return filled && noDupes;
  }, [q, a]);

  const handleNext = () => {
    if (!stepValid(step)) {
      toast.error("Please select a question and enter an answer.", { style: errorToastStyle, position: "top-center" });
      return;
    }
    if (step < 2) setStep(step + 1);
  };

  const handleBack = () => {
    if (step > 0) setStep(step - 1);
    else onBack?.();
  };

  const handleSubmit = async () => {
    if (!allValid) {
      toast.error("Pick 3 different questions and answer all.", { style: errorToastStyle, position: "top-center" });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ReqType: "TAOneApp_Rupay_SecurityQs",
        GeoLocation: locationGeo,
        MachineID: ip,
        EntityID: entity_id,
        Mobilenumber: String(mobileNumber),
        Platform,
        Statement: "UpdateSecurityQuestions",
        SessionID: sessionId || "",
        ApplicationType,
        RupayRefNo: rupayRefNo || "",
        SecurityQuestion: q[0],
        SecurityAnswer: a[0],
        SecurityQuestion2: q[1],
        SecurityAnswer2: a[1],
        SecurityQuestion3: q[2],
        SecurityAnswer3: a[2],
      };

      const res = await api.post(`${baseapi}/Forgotrupayquestions`, payload);

      if (String(res.data.Response_Code) === "1111") {
        toast.success(res.data.Message || "Security questions saved!", { style: successToastStyle, position: "top-center" });
        onSuccess?.();
      } else {
        toast.error(res.data.Message || "Failed to save security questions.", { style: errorToastStyle, position: "top-center" });
      }
    } catch {
      toast.error("Error saving security questions. Please try again.", { style: errorToastStyle, position: "top-center" });
    } finally {
      setSaving(false);
    }
  };

  // ── Tracker (1 — 2 — 3) ──
  const Tracker = () => (
    <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
      {[0, 1, 2].map((i) => {
        const done = i < step || (i === step && stepValid(i) && step === 2 ? false : i < step);
        const isDone = i < step;
        const active = i === step;
        return (
          <React.Fragment key={i}>
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                fontSize: 13,
                fontWeight: 700,
                color: isDone || active ? "#fff" : "#9ca3af",
                bgcolor: isDone ? "#16a34a" : active ? "#22c55e" : "#e5e7eb",
                transition: "0.2s",
              }}
            >
              {isDone ? <CheckIcon sx={{ fontSize: 16 }} /> : i + 1}
            </Box>
            {i < 2 && (
              <Box
                sx={{
                  flex: 1,
                  height: 3,
                  mx: 1,
                  borderRadius: 2,
                  bgcolor: i < step ? "#16a34a" : "#e5e7eb",
                  transition: "0.2s",
                }}
              />
            )}
          </React.Fragment>
        );
      })}
    </Box>
  );

  return (
    <Box
      sx={{
        width: { xs: "90%", sm: "420px" },
        background: "#fff",
        borderRadius: 4,
        p: 4,
        boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
        maxHeight: "90vh",
        overflowY: "auto",
      }}
    >
      <Typography variant="h5" fontWeight="bold">
        Set Security Questions
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Step {step + 1} of 3 — choose a question and enter its answer.
      </Typography>

      <Tracker />

      {/* Current step's question + answer */}
      <Box>
        <Typography sx={{ fontSize: 13, mb: 0.5 }}>Security Question {step + 1}</Typography>
        <Select
          fullWidth
          value={q[step]}
          onChange={(e) => setQuestionAt(step, e.target.value)}
          displayEmpty
          MenuProps={{ disablePortal: true, PaperProps: { sx: { zIndex: 3000, mt: 1 } } }}
          renderValue={(value) => (value ? value : <em>Select security question</em>)}
        >
          {optionsFor(step).map((question, i) => (
            <MenuItem key={i} value={question}>
              {question}
            </MenuItem>
          ))}
        </Select>

        <Box display="flex" justifyContent="space-between" mt={2}>
          <Typography sx={{ fontSize: 13 }}>Answer {step + 1}</Typography>
          <Typography sx={{ fontSize: 12 }} color="text.secondary">
            {a[step].length}/25
          </Typography>
        </Box>
        <TextField
          fullWidth
          disabled={!q[step]}
          value={a[step]}
          onChange={(e) => setAnswerAt(step, e.target.value)}
          placeholder="Enter your answer"
        />
      </Box>

      {/* Buttons */}
      <Box sx={{ display: "flex", gap: 1.5, mt: 3 }}>
        <Button
          fullWidth
          variant="outlined"
          onClick={handleBack}
          sx={{
            height: 48,
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 600,
            borderColor: "#e2e8f0",
            color: "#334155",
          }}
        >
          {step === 0 ? "Back" : "Previous"}
        </Button>

        {step < 2 ? (
          <Button
            fullWidth
            disabled={!stepValid(step)}
            onClick={handleNext}
            sx={{
              height: 48,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 700,
              background: !stepValid(step) ? "#d1d5db" : "linear-gradient(135deg, #16a34a, #22c55e)",
              color: !stepValid(step) ? "#888" : "#fff",
            }}
          >
            Next
          </Button>
        ) : (
          <Button
            fullWidth
            disabled={!allValid || saving}
            onClick={handleSubmit}
            sx={{
              height: 48,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 700,
              background: !allValid || saving ? "#d1d5db" : "linear-gradient(135deg, #16a34a, #22c55e)",
              color: !allValid || saving ? "#888" : "#fff",
            }}
          >
            {saving ? "Saving..." : "Submit"}
          </Button>
        )}
      </Box>
    </Box>
  );
}